// import type { BuildingModelConfig } from "@/components/mapbox/Lib/Objects/MapboxBuildingObject";
import * as THREE from "three";
import { degToRad } from "three/src/math/MathUtils.js";
import type { BuildingDimensions } from "@/types/building";

export const createFlatRoof = (
  dims: BuildingDimensions,
  roofColor: THREE.Color
): THREE.Group => {
  const group = new THREE.Group();
  const flatGeometry = new THREE.BoxGeometry(dims.width, 0.2, dims.length);
  const flatMaterial = new THREE.MeshLambertMaterial({
    color: roofColor,
  });
  const flatRoof = new THREE.Mesh(flatGeometry, flatMaterial);
  flatRoof.position.y = dims.height + 0.1;
  flatRoof.castShadow = true;
  group.add(flatRoof);
  return group;
};

export const createShedRoof = (
  dims: BuildingDimensions,
  roofColor: THREE.Color,
  exturdeColor: THREE.Color
): THREE.Group => {
  const angle = dims.roofAngleDeg ?? 20;
  const height = dims.width * Math.tan(degToRad(angle));
  const gableGroup = new THREE.Group();
  const rad = degToRad(angle);
  const topSpan = dims.width / Math.cos(rad);
  const flatGeometry1 = new THREE.BoxGeometry(topSpan, 0.2, dims.length);
  const flatMaterial1 = new THREE.MeshLambertMaterial({
    color: roofColor,
  });
  const flatRoof1 = new THREE.Mesh(flatGeometry1, flatMaterial1);
  flatRoof1.position.y = dims.height + height / 2;
  flatRoof1.castShadow = true;
  flatRoof1.rotateZ(degToRad(angle));
  gableGroup.add(flatRoof1);

  const shedShape = new THREE.Shape();
  shedShape.moveTo(-dims.width / 2, 0);
  shedShape.lineTo(dims.width / 2, 0);
  shedShape.lineTo(dims.width / 2, height);
  shedShape.lineTo(-dims.width / 2, 0);

  const shedGeometry = new THREE.ExtrudeGeometry(shedShape, {
    depth: dims.length,
    bevelEnabled: false,
  });
  const shedMaterial = new THREE.MeshLambertMaterial({
    color: exturdeColor,
  });
  const shedRoof = new THREE.Mesh(shedGeometry, shedMaterial);
  shedRoof.position.set(0, dims.height, -dims.length / 2);
  shedRoof.castShadow = true;
  gableGroup.add(shedRoof);

  return gableGroup;
};

export const createHipRoof = (
  dims: BuildingDimensions,
  roofColor: THREE.Color
): THREE.Group => {
  const group = new THREE.Group();

  const material = new THREE.MeshLambertMaterial({
    color: roofColor,
    side: THREE.DoubleSide,
  });
  const angle = dims.roofAngleDeg ?? 20;
  const height = (dims.width / 2) * Math.tan(degToRad(angle));

  // Calculate slope - left and right meet at center, front/back slope inward
  const slopeZ = dims.length * 0.25; // How much front/back faces slope inward

  // Left sloped face (slopes to center ridge)
  const customBufferGeometryLeft = new THREE.BufferGeometry();
  const dataLeft = [
    -dims.width / 2,
    0,
    -dims.length / 2, // bottom back corner
    -dims.width / 2,
    0,
    dims.length / 2, // bottom front corner
    0,
    height,
    dims.length / 2 - slopeZ, // ridge front (center)
    0,
    height,
    -dims.length / 2 + slopeZ, // ridge back (center)
  ];
  customBufferGeometryLeft.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(dataLeft, 3)
  );
  customBufferGeometryLeft.setIndex(
    new THREE.BufferAttribute(new Uint16Array([0, 1, 2, 2, 3, 0]), 1)
  );
  customBufferGeometryLeft.computeVertexNormals();
  const meshLeft = new THREE.Mesh(customBufferGeometryLeft, material);
  meshLeft.position.set(0, dims.height, 0);
  meshLeft.castShadow = true;
  group.add(meshLeft);

  // Right sloped face (slopes to center ridge)
  const customBufferGeometryRight = new THREE.BufferGeometry();
  const dataRight = [
    dims.width / 2,
    0,
    -dims.length / 2, // bottom back corner
    dims.width / 2,
    0,
    dims.length / 2, // bottom front corner
    0,
    height,
    dims.length / 2 - slopeZ, // ridge front (center)
    0,
    height,
    -dims.length / 2 + slopeZ, // ridge back (center)
  ];
  customBufferGeometryRight.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(dataRight, 3)
  );
  customBufferGeometryRight.setIndex(
    new THREE.BufferAttribute(new Uint16Array([0, 1, 2, 2, 3, 0]), 1)
  );
  customBufferGeometryRight.computeVertexNormals();
  const meshRight = new THREE.Mesh(customBufferGeometryRight, material);
  meshRight.position.set(0, dims.height, 0);
  meshRight.castShadow = true;
  group.add(meshRight);

  // Back sloped face (triangular, slopes to ridge)
  const customBufferGeometryBack = new THREE.BufferGeometry();
  const dataBack = [
    -dims.width / 2,
    0,
    -dims.length / 2, // bottom left corner
    dims.width / 2,
    0,
    -dims.length / 2, // bottom right corner
    0,
    height,
    -dims.length / 2 + slopeZ, // ridge point (center)
  ];
  customBufferGeometryBack.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(dataBack, 3)
  );
  customBufferGeometryBack.computeVertexNormals();
  const meshBack = new THREE.Mesh(customBufferGeometryBack, material);
  meshBack.position.set(0, dims.height, 0);
  meshBack.castShadow = true;
  group.add(meshBack);

  // Front sloped face (triangular, slopes to ridge)
  const customBufferGeometryFront = new THREE.BufferGeometry();
  const dataFront = [
    -dims.width / 2,
    0,
    dims.length / 2, // bottom left corner
    dims.width / 2,
    0,
    dims.length / 2, // bottom right corner
    0,
    height,
    dims.length / 2 - slopeZ, // ridge point (center)
  ];
  customBufferGeometryFront.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(dataFront, 3)
  );
  customBufferGeometryFront.computeVertexNormals();
  const meshFront = new THREE.Mesh(customBufferGeometryFront, material);
  meshFront.position.set(0, dims.height, 0);
  meshFront.castShadow = true;
  group.add(meshFront);

  return group;
};

export const createDormerRoof = (
  dims: BuildingDimensions,
  roofColor: THREE.Color
): THREE.Group => {
  const group = new THREE.Group();

  // Main gable roof
  // const mainGableShape = new THREE.Shape();
  // mainGableShape.moveTo(-dims.width / 2, 0);
  // mainGableShape.lineTo(dims.width / 2, 0);
  // mainGableShape.lineTo(0, dims.height * 0.3);
  // mainGableShape.lineTo(-dims.width / 2, 0);

  // const mainGableGeometry = new THREE.ExtrudeGeometry(mainGableShape, {
  //   depth: dims.length,
  //   bevelEnabled: false,
  // });
  // const mainGableMaterial = new THREE.MeshLambertMaterial({
  //   color: roofColor,
  // });
  // const mainGableRoof = new THREE.Mesh(mainGableGeometry, mainGableMaterial);
  // mainGableRoof.position.set(0, dims.height, dims.length / 2);
  // mainGableRoof.castShadow = true;
  // group.add(mainGableRoof);

  // Extended dormer section
  const roofWidth = 3;
  const even = Math.floor(dims.width / roofWidth);
  const lastItem = dims.width - even * roofWidth;
  const dormerMaterial = new THREE.MeshLambertMaterial({
    color: roofColor,
  });

  for (let i = 0; i < even; i++) {
    const dormerShape = new THREE.Shape();
    dormerShape.moveTo(dims.width / 2, 0);
    dormerShape.lineTo(dims.width / 2 + roofWidth, 2);
    dormerShape.lineTo(dims.width / 2 + roofWidth, 0);
    const dormerGeometry = new THREE.ExtrudeGeometry(dormerShape, {
      depth: dims.length,
      bevelEnabled: false,
    });

    const dormerRoof = new THREE.Mesh(dormerGeometry, dormerMaterial);
    dormerRoof.position.set(
      -dims.width + i * roofWidth,
      dims.height,
      -dims.length / 2
    );
    dormerRoof.castShadow = true;
    group.add(dormerRoof);
  }
  if (lastItem !== 0) {
    const dormerShape = new THREE.Shape();
    dormerShape.moveTo(dims.width / 2, 0);
    dormerShape.lineTo(dims.width / 2 + lastItem, 2);
    dormerShape.lineTo(dims.width / 2 + lastItem, 0);
    const dormerGeometry = new THREE.ExtrudeGeometry(dormerShape, {
      depth: dims.length,
      bevelEnabled: false,
    });
    const dormerRoof = new THREE.Mesh(dormerGeometry, dormerMaterial);
    dormerRoof.position.set(
      -dims.width + even * roofWidth,
      dims.height,
      -dims.length / 2
    );
    dormerRoof.castShadow = true;
    group.add(dormerRoof);
  }

  return group;
};

export const createGableRoof = (
  dims: BuildingDimensions,
  roofColor: THREE.Color,
  exturdeColor: THREE.Color
): THREE.Group => {
  const angle = dims.roofAngleDeg ?? 20;
  const height = dims.width * Math.tan(degToRad(angle));
  const group = new THREE.Group();
  // Mansard roof with double slope
  const mansardShape = new THREE.Shape();
  mansardShape.moveTo(-dims.width / 2, 0); // Bottom left
  mansardShape.lineTo(0, height); // Peak
  mansardShape.lineTo(dims.width / 2, 0); // Bottom right
  mansardShape.lineTo(dims.width / 2 - 0.4, 0);
  mansardShape.lineTo(0, height - 0.45); // Inner peak
  mansardShape.lineTo(-dims.width / 2 + 0.4, 0);
  mansardShape.lineTo(-dims.width / 2, 0);

  const mansardGeometry = new THREE.ExtrudeGeometry(mansardShape, {
    depth: dims.length,
    bevelEnabled: false,
  });
  const mansardMaterial = new THREE.MeshLambertMaterial({ color: roofColor });
  const mansardRoof = new THREE.Mesh(mansardGeometry, mansardMaterial);
  mansardRoof.renderOrder = 999999;
  mansardRoof.position.set(0, dims.height, -dims.length / 2);
  mansardRoof.castShadow = true;
  group.add(mansardRoof);

  const exturdeShape = new THREE.Shape();
  exturdeShape.moveTo(-dims.width / 2, 0);
  exturdeShape.lineTo(0, height - 0.45);
  exturdeShape.lineTo(dims.width / 2, 0);
  exturdeShape.lineTo(-dims.width / 2, 0);

  const exturdeGeometry = new THREE.ExtrudeGeometry(exturdeShape, {
    depth: dims.length,
    bevelEnabled: false,
  });
  const exturdeMaterial = new THREE.MeshLambertMaterial({
    color: exturdeColor,
  });

  const exturdeRoof = new THREE.Mesh(exturdeGeometry, exturdeMaterial);
  exturdeRoof.renderOrder = 10;
  exturdeRoof.position.set(0, dims.height, -dims.length / 2);
  exturdeRoof.castShadow = true;
  group.add(exturdeRoof);

  return group;
};

export const createMansardRoof = (
  dims: BuildingDimensions,
  roofColor: THREE.Color,
  exturdeColor: THREE.Color
): THREE.Group => {
  const angle = 20;
  const height = dims.width * Math.tan(degToRad(angle));
  const group = new THREE.Group();
  // Mansard roof with double slope
  const mansardShape = new THREE.Shape();
  mansardShape.moveTo(-dims.width / 2, -0); // Bottom left
  mansardShape.lineTo(-(dims.width + 2) / 4, height / 1.35); // Peak
  mansardShape.lineTo(0, height); // Peak
  mansardShape.lineTo((dims.width + 2) / 4, height / 1.35); // Peak
  mansardShape.lineTo(dims.width / 2, 0); // Bottom right

  mansardShape.lineTo(dims.width / 2 - 0.4, -0);
  mansardShape.lineTo((dims.width + 2) / 4 - 0.25, height / 1.35 - 0.15);
  mansardShape.lineTo(0, height - 0.3); // Inner peak
  mansardShape.lineTo(-(dims.width + 2) / 4 + 0.25, height / 1.35 - 0.15);
  mansardShape.lineTo(-dims.width / 2 + 0.4, 0);
  // mansardShape.lineTo(-dims.width / 2 + 0.25, -0.5);

  //

  const mansardGeometry = new THREE.ExtrudeGeometry(mansardShape, {
    depth: dims.length,
    bevelEnabled: false,
  });
  const mansardMaterial = new THREE.MeshLambertMaterial({ color: roofColor });
  const mansardRoof = new THREE.Mesh(mansardGeometry, mansardMaterial);
  mansardRoof.position.set(0, dims.height, -dims.length / 2);
  mansardRoof.castShadow = true;
  group.add(mansardRoof);

  const exturdeShape = new THREE.Shape();
  exturdeShape.moveTo(-dims.width / 2, 0);
  exturdeShape.lineTo(-dims.width / 4 - 0.2, height / 1.35 - 0.1);
  exturdeShape.lineTo(0, height - 0.3);
  exturdeShape.lineTo(dims.width / 4 + 0.2, height / 1.35 - 0.1);
  exturdeShape.lineTo(dims.width / 2, 0);
  exturdeShape.lineTo(-dims.width / 2, 0);

  const exturdeGeometry = new THREE.ExtrudeGeometry(exturdeShape, {
    depth: dims.length,
    bevelEnabled: false,
  });
  const exturdeMaterial = new THREE.MeshLambertMaterial({
    color: exturdeColor,
  });
  const exturdeRoof = new THREE.Mesh(exturdeGeometry, exturdeMaterial);
  exturdeRoof.position.set(0, dims.height, -dims.length / 2);
  exturdeRoof.castShadow = true;
  group.add(exturdeRoof);

  return group;
};

export const createRoundRoof = (
  dims: BuildingDimensions,
  roofColor: THREE.Color
): THREE.Group => {
  const roofHeight = 0.2;
  const group = new THREE.Group();
  const roundGeometry = new THREE.CylinderGeometry(
    dims.width / 2,
    dims.width / 2,
    roofHeight,
    32
  );
  const roundMaterial = new THREE.MeshLambertMaterial({ color: roofColor });
  const roundRoof = new THREE.Mesh(roundGeometry, roundMaterial);
  roundRoof.position.set(0, dims.height + roofHeight / 2, 0);
  roundRoof.castShadow = true;
  group.add(roundRoof);
  return group;
};
